import { Button } from 'datocms-react-ui';
import { useState } from 'react';

type Props = {
  translations: { [languageCode: string]: string } | undefined;
  onUpdate: (languageCode: string, value: string) => void;
  onRemove: (languageCode: string) => void;
};

type DraftRow = { lang: string; value: string };

export function PresetTranslationsEditor({
  translations,
  onUpdate,
  onRemove,
}: Props) {
  const entries = Object.entries(translations ?? {});
  const [draft, setDraft] = useState<DraftRow | null>(null);

  const commitDraft = () => {
    if (!draft) return;
    const lang = draft.lang.trim();
    const value = draft.value.trim();
    if (lang && value) {
      onUpdate(lang, value);
    }
    setDraft(null);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginTop: '6px',
        paddingLeft: '8px',
        borderLeft: '2px solid var(--border-color)',
      }}
    >
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--disabled-text-color)',
        }}
      >
        Preset translations
      </span>

      {entries.length === 0 && !draft && (
        <span style={{ fontSize: '11px', color: 'var(--disabled-text-color)' }}>
          No preset translations.
        </span>
      )}

      {entries.map(([lang, value]) => (
        <div
          key={lang}
          style={{ display: 'flex', gap: '6px', alignItems: 'center' }}
        >
          <input
            type="text"
            value={lang}
            disabled
            style={{ width: '60px', fontSize: '12px' }}
          />
          <input
            type="text"
            defaultValue={value}
            onBlur={e => {
              const next = e.target.value.trim();
              if (next && next !== value) {
                onUpdate(lang, next);
              } else if (!next) {
                onRemove(lang);
              }
            }}
            style={{ flex: 1, fontSize: '12px' }}
          />
          <button
            type="button"
            onClick={() => onRemove(lang)}
            aria-label={`Remove ${lang} translation`}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'var(--disabled-text-color)',
            }}
          >
            ×
          </button>
        </div>
      ))}

      {draft && (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            type="text"
            value={draft.lang}
            placeholder="es"
            onChange={e => setDraft({ ...draft, lang: e.target.value })}
            style={{ width: '60px', fontSize: '12px' }}
            autoFocus
          />
          <input
            type="text"
            value={draft.value}
            placeholder="Translated value"
            onChange={e => setDraft({ ...draft, value: e.target.value })}
            onBlur={commitDraft}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDraft();
              } else if (e.key === 'Escape') {
                setDraft(null);
              }
            }}
            style={{ flex: 1, fontSize: '12px' }}
          />
        </div>
      )}

      {!draft && (
        <Button
          type="button"
          buttonSize="xxs"
          onClick={() => setDraft({ lang: '', value: '' })}
          style={{ alignSelf: 'flex-start', marginTop: '4px' }}
        >
          + Add language
        </Button>
      )}
    </div>
  );
}
